import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { isThemeFlagOn } from "../../../pages/gameSetupStorage";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const MIN_POINTS = 1;
const MAX_POINTS = 100;
const MAX_THANKYOU_CHARS = 300;

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const normalizeCountInput = (raw) => String(raw).replace(/\D/g, "");

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [thankYouText, setThankYouText] = useState("");
  const [correctPoints, setCorrectPoints] = useState(String(MIN_POINTS));
  const [boxTextColor, setBoxTextColor] = useState("#000000");
  const [titleRulesColor, setTitleRulesColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#e9695e");
  const [showLeaderboard, setShowLeaderboard] = useState(true);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    setThankYouText(String(data.custom_text_thank_you_page ?? "").slice(0, MAX_THANKYOU_CHARS));
    const savedPoints = parseInt(data.correctPoints ?? data.points, 10) || MIN_POINTS;
    setCorrectPoints(String(Math.min(MAX_POINTS, Math.max(MIN_POINTS, savedPoints))));
    setBoxTextColor(toColorInputValue(data.landing_page_title_color));
    setTitleRulesColor(toColorInputValue(data.title_color));
    setButtonColor(toColorInputValue(data.button_color || "#e9695e"));
    setShowLeaderboard(isThemeFlagOn(data.leaderboard, true));
  }, [data]);

  const validate = () => {
    const errors = [];

    if (thankYouText.trim().length > MAX_THANKYOU_CHARS) {
      errors.push(`Thank you page text exceeds ${MAX_THANKYOU_CHARS} characters`);
    }
    const points = parseInt(correctPoints, 10);
    if (correctPoints === "" || Number.isNaN(points) || points < MIN_POINTS || points > MAX_POINTS) {
      errors.push(`Points per correct answer must be between ${MIN_POINTS} and ${MAX_POINTS}.`);
    }

    if (errors.length) return { ok: false, msg: errors.join("\n") };

    const payload = {
      landing_page_title_color: boxTextColor,
      title_color: titleRulesColor,
      button_color: buttonColor,
      points: parseInt(correctPoints, 10),
      custom_text_thank_you_page: thankYouText.trim(),
      leaderboard: showLeaderboard ? "true" : "false",
    };

    return { ok: true, payload };
  };

  const handleSave = async ({ goNext = false } = {}) => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data Updated", "success");
      if (goNext) navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };


  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="rules-shell">
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="rules-page-header">
        <h1 className="rules-page-header-title">Game Settings</h1>
        <p className="rules-page-header-desc">
          Scoring, what players see when the game ends, and the theme colors. Each
          stage's title and rules are set in Game Content.
        </p>
      </div>

      <div className="gs-layout">
        {/* 1 · Scoring */}
        <section className="rules-card gs-area-scoring" data-tour="tour-theme-settings">
          <header className="rules-card-header">
            <span className="rules-card-icon">
              <i className="fa-solid fa-star" aria-hidden />
            </span>
            <div>
              <h2 className="rules-card-title">Scoring</h2>
              <p className="rules-card-subtitle">How many points each correct answer is worth</p>
            </div>
          </header>
          <div className="rules-card-body">
            <div className="gs-row">
              <div className="gs-row-text">
                <label htmlFor="rules-correct-points" className="gs-row-label">
                  Points per correct answer
                </label>
                <p className="gs-row-hint" id="rules-correct-points-range">
                  Used in every stage. The maximum score is the number of questions × this value.
                  Min {MIN_POINTS}, max {MAX_POINTS}.
                </p>
              </div>
              <input
                id="rules-correct-points"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={String(MAX_POINTS).length}
                className="rules-setting-input"
                value={correctPoints}
                onChange={(e) => {
                  const digits = normalizeCountInput(e.target.value);
                  // Never let the value go above the max while typing.
                  setCorrectPoints(
                    digits && parseInt(digits, 10) > MAX_POINTS ? String(MAX_POINTS) : digits
                  );
                }}
                onBlur={() => {
                  const n = parseInt(correctPoints, 10);
                  if (correctPoints === "" || Number.isNaN(n) || n < MIN_POINTS) {
                    setCorrectPoints(String(MIN_POINTS));
                  } else if (n > MAX_POINTS) {
                    setCorrectPoints(String(MAX_POINTS));
                  }
                }}
                aria-describedby="rules-correct-points-range"
              />
            </div>
          </div>
        </section>

        {/* 2 · End of game */}
        <section className="rules-card gs-area-end">
          <header className="rules-card-header">
            <span className="rules-card-icon">
              <i className="fa-solid fa-flag-checkered" aria-hidden />
            </span>
            <div>
              <h2 className="rules-card-title">End of game</h2>
              <p className="rules-card-subtitle">What comes after the score on the thank-you page</p>
            </div>
          </header>
          <div className="rules-card-body">
            <div className="gs-row">
              <div className="gs-row-text">
                <label htmlFor="rules-show-leaderboard" className="gs-row-label">
                  Show leaderboard
                </label>
                <p className="gs-row-hint">Let players compare their score with others.</p>
              </div>
              <label className="rules-switch">
                <input
                  id="rules-show-leaderboard"
                  type="checkbox"
                  role="switch"
                  checked={showLeaderboard}
                  onChange={(e) => setShowLeaderboard(e.target.checked)}
                  disabled={loading}
                />
                <span className="rules-switch-slider" aria-hidden />
              </label>
            </div>

            {/* The flow players get with the current setting. */}
            <ol className="gs-flow" aria-label="Player flow">
              <li className="gs-flow-step">Thank-you page with score</li>
              <li className="gs-flow-step gs-flow-step--btn">
                <i className={`fa-solid ${showLeaderboard ? "fa-trophy" : "fa-hand-pointer"}`} aria-hidden />
                {showLeaderboard ? "Leaderboard" : "Next"} button
              </li>
              <li className="gs-flow-step gs-flow-step--end">
                {showLeaderboard ? "Ranking table" : "Closing thank-you message"}
              </li>
            </ol>

            {showLeaderboard ? (
              <p className="gs-note">
                <i className="fa-solid fa-circle-info" aria-hidden />
                Turn the leaderboard off to show a closing thank-you message instead.
              </p>
            ) : (
              <div className="rules-field-group gs-nested">
                <label className="gs-row-label" htmlFor="rules-thankyou-text">
                  Closing message
                </label>
                <textarea
                  id="rules-thankyou-text"
                  className="rules-textarea gs-textarea"
                  maxLength={MAX_THANKYOU_CHARS}
                  rows={3}
                  placeholder="e.g. Thanks for celebrating with us — see you next year!"
                  value={thankYouText}
                  onChange={(e) => setThankYouText(e.target.value)}
                />
                <p className="gs-row-hint gs-count">
                  Shown under “Thank you for playing!” · {thankYouText.length}/{MAX_THANKYOU_CHARS}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* 3 · Colors */}
        <section className="rules-card gs-area-colors" data-tour="tour-page-content">
          <header className="rules-card-header">
            <span className="rules-card-icon">
              <i className="fa-solid fa-palette" aria-hidden />
            </span>
            <div>
              <h2 className="rules-card-title">Colors</h2>
              <p className="rules-card-subtitle">Click a swatch to change it</p>
            </div>
          </header>
          <div className="rules-card-body gs-colors">
            {[
              {
                label: "Stage titles",
                hint: "Title and subtitle at the top of every stage",
                value: titleRulesColor,
                set: setTitleRulesColor,
              },
              {
                label: "Buttons",
                hint: "Buttons, timer and points pills, and highlights",
                value: buttonColor,
                set: setButtonColor,
              },
              {
                label: "Rules popup text",
                hint: "Text inside each stage's “How to play” popup",
                value: boxTextColor,
                set: setBoxTextColor,
              },
            ].map((c) => (
              <label key={c.label} className="gs-color-row">
                <span className="gs-swatch" style={{ background: c.value }}>
                  <input type="color" value={c.value} onChange={(e) => c.set(e.target.value)} />
                </span>
                <span className="gs-row-text">
                  <span className="gs-row-label">{c.label}</span>
                  <span className="gs-row-hint">{c.hint}</span>
                </span>
                <code className="gs-hex">{c.value.toUpperCase()}</code>
              </label>
            ))}
          </div>
        </section>
      </div>

      <div className="rules-sticky-actions" data-tour="tour-actions">
        <button
          type="button"
          className="save-and-continue"
          onClick={() => handleSave({ goNext: true })}
          disabled={loading}
        >
          {loading ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" aria-hidden />
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </div>
  );
};

export default Rules;
